import { ReactNode, RefObject, useCallback, useRef } from 'react';
import { MenuItem } from '@szhsin/react-menu';
import styled from 'styled-components';
import {
  useUnifiedOpenOrdersInitialLoadStatus,
  useUnifiedOpenOrdersIdsList,
} from '@paradigm/features/src/unified-rfqs/contexts/unified-orders';
import { Order } from '@paradigm/features/src/unified-rfqs/entities/shared';
import FillScrollable from '#/components/FillScrollable';
import ActionsBarEmpty from '#/components/UnifiedRfqs/components/bottom-tabs/ActionsBarEmpty';
import { useUMPlatformCreationDisabled } from '@paradigm/features/src/platform-state/contexts/providers';
import {
  TrackingCtxProvider,
  useTrackedCallback,
  useTrackAction,
} from '@paradigm/features/src/analytics';
import { useQuoteBuilderModalMethods as useObQuoteBuilderModalActions } from '@paradigm/features/src/unified-rfqs/ui/grfq/quote-builder/modal-context';
import { useQuoteBuilderModalMethods as useRfqQuoteBuilderModalActions } from '@paradigm/features/src/unified-rfqs/ui/drfq/quote-builder/modal-context';
import OrderRow, {
  ObContextMenuData,
  OrderHeaderRow,
  RfqContextMenuData,
} from '#/components/UnifiedRfqs/open-orders/OrderRow';
import ContextMenuProvider, {
  ContextMenu as BaseContextMenu,
  useContextMenuActions,
  useContextMenuView,
} from '#/components/UnifiedRfqs/context-menu/context-menu';
import BottomLoader from '#/components/UnifiedRfqs/open-orders/BottomLoader';
import CreateMarketMenuItem from '#/components/UnifiedRfqs/context-menu/CreateMarketMenuItem';

function ContextMenu() {
  const { createRfq } = useContextMenuActions();
  const { data } = useContextMenuView<ObContextMenuData | RfqContextMenuData>();
  const datadogPrefix = 'Open Orders Table Context Menu';
  const isPlatformCreationDisabled = useUMPlatformCreationDisabled();
  const { open: openObQuoteBuilder } = useObQuoteBuilderModalActions();
  const { open: openRfqQuoteBuilder } = useRfqQuoteBuilderModalActions();
  const handleCreateRFQ = useTrackedCallback(
    'create market',
    () => createRfq('RFQ'),
    { auctionType: 'RFQ' },
  );

  const handleCreateOB = useTrackedCallback(
    'create market',
    () => createRfq('OB'),
    { auctionType: 'OB' },
  );

  const track = useTrackAction();

  const handleViewDetails = useCallback(() => {
    if (data == null) return;
    const { auctionType } = data;
    track('view quote builder', { auctionType });

    if (auctionType === 'OB') {
      const { quote } = data;
      openObQuoteBuilder({
        rfqId: quote.rfq_id,
        clickedQuote: quote,
        side: quote.side,
      });
      return;
    }

    const { order } = data;
    openRfqQuoteBuilder({
      rfqId: order.rfq_id,
      side: order.side,
    });
  }, [data, openObQuoteBuilder, openRfqQuoteBuilder, track]);

  return (
    <BaseContextMenu>
      <CreateMarketMenuItem
        type="RFQ"
        datadogPrefix={datadogPrefix}
        onClick={handleCreateRFQ}
      />
      <CreateMarketMenuItem
        type="OB"
        datadogPrefix={datadogPrefix}
        onClick={handleCreateOB}
      />

      <MenuItem
        onClick={handleViewDetails}
        disabled={isPlatformCreationDisabled}
      >
        View Details
      </MenuItem>
    </BaseContextMenu>
  );
}

export default function OpenOrders() {
  const { error, isLoadingLatest } = useUnifiedOpenOrdersInitialLoadStatus();
  const ordersIds = useUnifiedOpenOrdersIdsList();
  const scrollableRef = useRef<HTMLDivElement>(null);
  const hasError = error.length > 0;

  const content: ReactNode = isLoadingLatest ? null : hasError ? (
    <pre css="color: red; margin-top: 1rem; text-align: center">{error}</pre>
  ) : ordersIds.length === 0 ? (
    <p css="opacity: 0.7; margin-top: 1rem; text-align: center">
      You have no open orders
    </p>
  ) : (
    <OrderRows scrollableRef={scrollableRef} ordersIds={ordersIds} />
  );

  return (
    <TrackingCtxProvider value="open orders tab">
      <Container>
        <ActionsBarEmpty />
        <FillScrollable
          ref={scrollableRef}
          data-testid="unified-rfq-open-orders-table"
        >
          <OrderHeaderRow />
          <ContextMenuProvider>
            {content}
            <ContextMenu />
          </ContextMenuProvider>
          <BottomLoader scrollableRef={scrollableRef} />
        </FillScrollable>
      </Container>
    </TrackingCtxProvider>
  );
}

const Container = styled.div`
  flex: 1; // take 100% heigh
  display: flex;
  flex-direction: column;
`;

function OrderRows(props: {
  readonly scrollableRef: RefObject<HTMLDivElement>;
  readonly ordersIds: readonly Order['id'][];
}) {
  return (
    <>
      {props.ordersIds.map((id) => (
        <OrderRow key={id} orderId={id} scrollableRef={props.scrollableRef} />
      ))}
    </>
  );
}
